/**
 * Question Generation Utilities
 * Separate functions for generating different types of questions
 */

import { IConfigOption } from '../types/configOption';
import { IProduct } from '../types/productSetup';
import { ICategory } from '../types/categorySetup';
import { IFlowQuestion, IFlowOption } from './jsonFlowGenerator';
import { DEFAULT_OPTION_UNIT } from '../constants';
import { OPTION_TYPE_QUESTION_MAP, OPTION_TYPE_HELPER_TEXT_MAP } from '../constants/questionTextMap';

const parsePriceList = (raw?: string): any[] | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Helper function to parse displayCondition from JSON string
 */
export const parseDisplayCondition = (conditionString?: string): any => {
  if (!conditionString) return undefined;
  try {
    return JSON.parse(conditionString);
  } catch {
    return undefined;
  }
};

/**
 * Build a priceList entry for unglazed pricing:
 * "when any Unglazed option is selected → use unglazedPrice"
 */
const buildUnglazedPriceList = (
  unglazedPrice: number | undefined,
  allConfigOptions: IConfigOption[],
): any[] | undefined => {
  if (!unglazedPrice || !allConfigOptions.length) return undefined;

  const unglazedOptions = allConfigOptions.filter((opt) =>
    opt.dogma_optiontype === 62 &&
    opt.dogma_name?.toLowerCase().includes("unglazed")
  );
  if (unglazedOptions.length === 0) return undefined;

  const byCategory = new Map<string, string[]>();
  unglazedOptions.forEach((opt) => {
    const catId = opt._dogma_category_value || "";
    if (!catId) return;
    if (!byCategory.has(catId)) byCategory.set(catId, []);
    byCategory.get(catId)!.push(opt.dogma_configurationoptionsid);
  });

  const conditions = Array.from(byCategory.entries()).map(
    ([categoryId, optionIds]) => ({ categoryId, optionIds })
  );
  return conditions.length > 0 ? [{ conditions, price: unglazedPrice }] : undefined;
};

/**
 * 1. Generate First Two Questions (Product Selection)
 */
export const generateFirstTwoQuestions = (
  products: IProduct[],
  categories: ICategory[],
  startQuestionCounter: number,
  getStepForQuestion: (key: string) => number,
  allConfigOptions: IConfigOption[] = [],
): IFlowQuestion[] => {
  const questions: IFlowQuestion[] = [];

  if (products.length === 0) {
    return questions;
  }

  // Map producttype number to type string
  const getProductTypeString = (producttype: number): string => {
    const typeMap: Record<number, string> = {
      1: "standard",
      2: "arch",
      3: "bays",
      4: "coupled",
    };
    return typeMap[producttype] || "standard";
  };

  // Group products by productType
  const productsByType = products.reduce((acc, product) => {
    const type = getProductTypeString(product.producttypecode);
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(product);
    return acc;
  }, {} as Record<string, IProduct[]>);

  // Product type display names and descriptions
  const productTypeInfo: Record<
    string,
    { label: string; description: string }
  > = {
    standard: { label: "Standard Window", description: "Single frame window" },
    arch: { label: "Arch Window", description: "Window with arched top" },
    bays: { label: "Bay Window", description: "Multi-frame bay configuration" },
    coupled: {
      label: "Coupled Window",
      description: "Multiple windows coupled together",
    },
  };

  // Question 1: Window Type Selection
  // Only create options for product types that have at least one product
  const productTypeEntries = Object.entries(productsByType).filter(
    ([_, typeProducts]) => typeProducts.length > 0
  );
  const standardProducts = products.filter(
    (product) => product.producttypecode == 1
  );
  const isSingleStandardProduct = standardProducts.length == 1;
  const windowTypeOptions: IFlowOption[] = productTypeEntries.map(
    ([type, typeProducts], index) => {
      // Use the product with the fewest frames as the representative for all fields
      const smallestFrameProduct = typeProducts.reduce(
        (min, p) => (p.quantityonhand || 1) < (min.quantityonhand || 1) ? p : min,
        typeProducts[0]
      );
      const maxFrames = Math.max(
        ...typeProducts.map((p) => p.quantityonhand || 1)
      );
      const info = productTypeInfo[type] || { label: type, description: "" };

      // For single standard product, use productid as optionId
      const optionId =
        type === "standard" && isSingleStandardProduct && standardProducts[0]
          ? standardProducts[0].productid
          : `A000${index + 1}`;

      let windowJson: any = undefined;
      if (smallestFrameProduct?.dogma_proudctwindowjson) {
        try {
          windowJson = JSON.parse(smallestFrameProduct.dogma_proudctwindowjson);
        } catch {
          // leave undefined if stored value is not valid JSON
        }
      }

      return {
        optionId,
        optionText: info.label,
        description: info.description,
        price: smallestFrameProduct?.price || 0,
        priceList: buildUnglazedPriceList(smallestFrameProduct?.dogma_unglazedprice, allConfigOptions),
        responseValue: type,
        optionType: 0,
        optionTypeName: "Window Type",
        thumbnail: smallestFrameProduct?.dogma_productimageurl,
        withHornsUrl: smallestFrameProduct?.dogma_withhornsurl,
        withoutHornsUrl: smallestFrameProduct?.dogma_withouthornsurl,
        validProducts: typeProducts.map((p) => p.productid),
        numberOfFrames: maxFrames,
        numberOfSashes: smallestFrameProduct?.dogma_numberofsash,
        producttypecode: smallestFrameProduct?.producttypecode,
        windowJson,
        order: index + 1,
      };
    }
  );

  const allProductIds = products.map((p) => p.productid);
  const windowTypeCategory = categories.find(
    (item) => item.dogma_categorytype === 1
  );
  // Use responsePath as the unique key for step preservation
  const responsePathKey = "window_type.selection";

  const windowTypeQuestion: IFlowQuestion = {
    questionId: `Q${String(startQuestionCounter).padStart(4, "0")}`,
    step: getStepForQuestion(responsePathKey),
    category: windowTypeCategory?.dogma_categoryid ?? "",
    categoryName: windowTypeCategory?.dogma_name ?? "",
    optionType: 0,
    optionTypeName: "Window Type",
    question: "What type of window would you like?",
    questionType: 5, // Single Select (Dropdown)
    required: true,
    responsePath: responsePathKey,
    displayCondition: {
      enabled: true,
      dependsOn: [],
      validForProducts: allProductIds,
    },
    options: windowTypeOptions,
  };

  questions.push(windowTypeQuestion);

  // Question 2: Frame Count Selection
  const frameCountOptions: IFlowOption[] = [];

  // Get single standard product ID to exclude from frame count question
  const singleStandardProductId = isSingleStandardProduct && standardProducts[0]
    ? standardProducts[0].productid
    : null;

  // Create a map of product type → representative product ID (from window type options)
  const typeToWindowTypeOptionId = new Map<string, string>();
  windowTypeOptions.forEach((opt) => {
    const productType = opt.responseValue;
    typeToWindowTypeOptionId.set(productType, opt.optionId);
  });

  let frameOptionOrder = 1;
  Object.entries(productsByType).forEach(([type, typeProducts]) => {
    const sortedProducts = [...typeProducts].sort(
      (a, b) => (a.quantityonhand || 1) - (b.quantityonhand || 1)
    );

    sortedProducts.forEach((product) => {
      // Skip single standard product - it's already selected in question 1
      if (product.productid === singleStandardProductId) {
        return;
      }

      const frameCount = product.quantityonhand || 1;
      const info = productTypeInfo[type] || { label: type, description: "" };

      let optionLabel = product.name;
      if (type === "bays" || type === "coupled") {
        optionLabel = `${frameCount} ${frameCount === 1 ? "Frame" : "Frames"}`;
      }

      // Get the window type option ID for this product type
      const windowTypeOptionId = typeToWindowTypeOptionId.get(type);

      let windowJson: any = undefined;
      if (product.dogma_proudctwindowjson) {
        try {
          windowJson = JSON.parse(product.dogma_proudctwindowjson);
        } catch {
          // leave undefined if stored value is not valid JSON
        }
      }

      frameCountOptions.push({
        optionId: product.productid,
        optionText: optionLabel,
        description:
          product.description ||
          `${info.label} - ${frameCount} frame${frameCount > 1 ? "s" : ""}`,
        price: product.price || 0,
        priceList: buildUnglazedPriceList(product.dogma_unglazedprice, allConfigOptions),
        responseValue: product.productid,
        optionType: 64,
        optionTypeName: "Variation",
        validProducts: [product.productid],
        numberOfFrames: frameCount,
        numberOfSashes: product.dogma_numberofsash,
        producttypecode: product.producttypecode,
        thumbnail: product.dogma_productimageurl,
        withHornsUrl: product.dogma_withhornsurl,
        withoutHornsUrl: product.dogma_withouthornsurl,
        windowJson,
        displayCondition: windowTypeOptionId && windowTypeCategory?.dogma_categoryid
          ? {
              enabled: true,
              dependsOn: [
                {
                  categoryId: windowTypeCategory.dogma_categoryid,
                  optionIds: [windowTypeOptionId],
                },
              ],
            }
          : undefined,
        order: frameOptionOrder++,
      });
    });
  });

  // Only add frame count question if there are options (excluding single standard product)
  if (frameCountOptions.length > 0) {
    // Filter out single standard product ID from validForProducts
    const validProductsForFrameCount = singleStandardProductId
      ? allProductIds.filter(id => id !== singleStandardProductId)
      : allProductIds;

    // Use responsePath as the unique key for step preservation
    const responsePathKey = "product_selection.frame_count";

    const frameCountQuestion: IFlowQuestion = {
      questionId: `Q${String(startQuestionCounter + 1).padStart(4, "0")}`,
      step: getStepForQuestion(responsePathKey),
      category: windowTypeCategory?.dogma_categoryid ?? "",
      categoryName: windowTypeCategory?.dogma_name ?? "",
      optionType: 64,
      optionTypeName: "Variation",
      question: "How many frames for your window?",
      questionType: 5, // Single Select (Dropdown)
      required: true,
      responsePath: responsePathKey,
      displayCondition: {
        enabled: true,
        dependsOn: [],
        validForProducts: validProductsForFrameCount,
      },
      options: frameCountOptions,
    };

    questions.push(frameCountQuestion);
  }

  return questions;
};

/**
 * 2. Generate Normal Question (Non-Repeating)
 */
export const generateNormalQuestion = (
  sortedOptions: IConfigOption[],
  category: ICategory,
  optionTypeValue: number | undefined,
  optionTypeName: string,
  _typeKey: string,
  inputTypeKey: string,
  questionCounter: number,
  getStepForQuestion: (key: string) => number
): IFlowQuestion => {
  // Determine question type
  const questionType = mapInputTypeToQuestionType(inputTypeKey);

  // Prepare options
  const preparedOptions: IFlowOption[] = sortedOptions.map((opt, index) => ({
    optionId: `${opt.dogma_configurationoptionsid}`,
    optionText: opt.dogma_name,
    description: opt.dogma_description,
    priceModifier:
      opt.dogma_pricemodifier !== 0 ? opt.dogma_pricemodifier : undefined,
    basePrice: opt.dogma_pricemodifier === 0 ? 0 : undefined,
    priceType: undefined,
    responseValue: opt.dogma_name.toLowerCase().replace(/\s+/g, "_"),
    value: opt.dogma_value,
    inputType: opt.dogma_inputtype,
    optionType: optionTypeValue,
    optionTypeName,
    thumbnail: opt.dogma_picturebloburl,
    displayCondition: parseDisplayCondition(
      opt.dogma_displaycondition ?? undefined
    ),
    order: index + 1,
    priceList: parsePriceList(opt.dogma_pricelist),
    optionUnit: opt.dogma_optionunit ?? DEFAULT_OPTION_UNIT,
  }));

  // Check if all options have the same display condition
  const { commonCondition, hasCommonCondition } =
    extractCommonDisplayCondition(preparedOptions);

  // If full common condition not found, union all optionIds per categoryId
  // across all options that have a dependsOn. This ensures the question is
  // shown whenever ANY option inside it could possibly be visible.
  // Example: 8 options depend on [a,b,c] and 1 depends on [a] only →
  //   intersection gives [a] (question hidden when b or c selected — wrong)
  //   union gives [a,b,c] (question shown for all three — correct)
  const dependsOnUnion: any[] | null = hasCommonCondition
    ? null
    : extractDependsOnUnion(preparedOptions);

  // Generate responsePath (use as unique key for step preservation)
  // Use numeric optionTypeValue (not optionTypeName) to avoid collisions when
  // multiple option types fall back to the same "Other" name.
  const responsePath = `${category.dogma_categoryid}.${optionTypeValue ?? 0}.${inputTypeKey}`;

  const question: IFlowQuestion = {
    questionId: `Q${String(questionCounter).padStart(4, "0")}`,
    step: getStepForQuestion(responsePath),
    category: category.dogma_categoryid,
    categoryName: category.dogma_name,
    optionType: optionTypeValue || 0,
    optionTypeName,
    question: generateQuestionText(
      category.dogma_name,
      optionTypeName,
      optionTypeValue,
      inputTypeKey
    ),
    helperText: optionTypeValue ? OPTION_TYPE_HELPER_TEXT_MAP[optionTypeValue] : undefined,
    questionType,
    required: true,
    responsePath,
    displayCondition: hasCommonCondition
      ? commonCondition
      : dependsOnUnion
      ? { enabled: true, dependsOn: dependsOnUnion }
      : undefined,
    options: preparedOptions.map((opt) => ({
      ...opt,
      // Remove displayCondition from options only when the full common condition
      // was lifted to the question level. For Glazing Type union and partial
      // intersection lifting, keep each option's own displayCondition so
      // per-option filtering still works inside the shown question.
      displayCondition: hasCommonCondition ? undefined : opt.displayCondition,
    })),
  };

  return question;
};

/**
 * 3. Generate Per-Window Questions (Repeating)
 */
export const generatePerWindowQuestions = (
  sortedOptions: IConfigOption[],
  category: ICategory,
  optionTypeValue: number | undefined,
  optionTypeName: string,
  _typeKey: string,
  inputTypeKey: string,
  questionCounter: number,
  getStepForQuestion: (key: string) => number,
  maxFrames: number,
  products: IProduct[],
  isConditionalGroup: boolean = false,
  allConfigOptions: IConfigOption[] = [],
): {
  questions: IFlowQuestion[];
  newQuestionCounter: number;
} => {
  const questions: IFlowQuestion[] = [];
  let currentQuestionCounter = questionCounter;

  // Find Configuration Mode options (optionType 49) FOR THIS CATEGORY ONLY
  const configModeOptions = allConfigOptions.filter(
    (opt) =>
      opt.dogma_optiontype === 49 &&
      opt._dogma_category_value === category.dogma_categoryid,
  );
  const individualOption = configModeOptions.find(
    (opt) =>
      opt.dogma_name?.toLowerCase().includes("individual") ||
      opt.dogma_value?.toLowerCase().includes("individual"),
  );
  const sameForAllOption = configModeOptions.find(
    (opt) =>
      opt.dogma_name?.toLowerCase().includes("same") ||
      opt.dogma_value?.toLowerCase().includes("same"),
  );

  // Get the configuration mode category ID dynamically
  const configModeCategoryId =
    configModeOptions[0]?._dogma_category_value ||
    configModeOptions[0]?.dogma_categoryid;

  // Determine question type
  const questionType = mapInputTypeToQuestionType(inputTypeKey);

  // Split ALL products by producttypecode (don't filter by options' validProducts):
  // - Standard (1) and Arch (2): Always single frame
  // - Bays (3) and Coupled (4): Multiple frames (use quantityonhand)
  // Individual options will handle their own validProducts restrictions via displayConditions
  const singleFrameProducts = products.filter(
    (p) => p.producttypecode === 1 || p.producttypecode === 2,
  );
  const multiFrameProducts = products.filter(
    (p) => p.producttypecode === 3 || p.producttypecode === 4,
  );

  // Check if these options are restricted to ONLY single-frame products
  // If so, skip per-window questions and Configuration Mode entirely
  const optionsValidProducts = sortedOptions
    .map((opt) => {
      const condition = parseDisplayCondition(
        opt.dogma_displaycondition ?? undefined,
      );
      return condition?.validForProducts || [];
    })
    .filter((arr) => arr.length > 0);

  // Build a unified set of valid product IDs from options' display conditions.
  // null means no restriction (all products are valid).
  const allValidProductIds: Set<string> | null =
    optionsValidProducts.length > 0
      ? new Set(optionsValidProducts.flat())
      : null;

  let hasMultiFrameProducts = false;
  if (allValidProductIds) {
    hasMultiFrameProducts = multiFrameProducts.some((p) =>
      allValidProductIds.has(p.productid),
    );
  } else {
    // No restrictions, assume all products including multi-frame
    hasMultiFrameProducts = multiFrameProducts.length > 0;
  }

  // Preserve only width restrictions when dependsOn/validForProducts are lifted to
  // question level — widthMin/widthMax must stay on the option so the chatbot can
  // hide/show individual options based on the measured window width (e.g. trickle vents).
  const widthOnlyCondition = (cond: any): any | undefined => {
    if (!cond) return undefined;
    const { widthMin, widthMax } = cond;
    if (widthMin == null && widthMax == null) return undefined;
    return { enabled: true, ...(widthMin != null && { widthMin }), ...(widthMax != null && { widthMax }) };
  };

  // If every option shares the same widthMin/widthMax, return those values so they
  // can be added to the question-level displayCondition (hides the whole question,
  // not just individual options, when the window is too narrow).
  const extractCommonWidth = (opts: IFlowOption[]): { widthMin?: number; widthMax?: number } => {
    const widths = opts.map((o) => ({
      widthMin: (o.displayCondition as any)?.widthMin as number | undefined,
      widthMax: (o.displayCondition as any)?.widthMax as number | undefined,
    }));
    const firstMin = widths[0]?.widthMin;
    const firstMax = widths[0]?.widthMax;
    const allSameMin = widths.every((w) => w.widthMin === firstMin);
    const allSameMax = widths.every((w) => w.widthMax === firstMax);
    return {
      ...(allSameMin && firstMin != null && { widthMin: firstMin }),
      ...(allSameMax && firstMax != null && { widthMax: firstMax }),
    };
  };

  // If options are ONLY for single-frame products, skip per-window questions
  if (!hasMultiFrameProducts) {
    // Skip per-window questions entirely, only generate "Same for All" question below
    // (will be handled in the "Same for All" section)
  } else {
    // Generate per-frame questions (only for products with more than 1 frame)
    for (let frameIndex = 1; frameIndex <= maxFrames; frameIndex++) {
      // Filter products that have at least this many frames, respecting validForProducts restrictions
      const validProductsForFrame = multiFrameProducts
        .filter((p) => (p.quantityonhand || 1) >= frameIndex)
        .filter(
          (p) => !allValidProductIds || allValidProductIds.has(p.productid),
        )
        .map((p) => p.productid);

      // Skip this frame question if no multi-frame products support it
      if (validProductsForFrame.length === 0) continue;

      // Prepare options with merged displayConditions
      const preparedOptions: IFlowOption[] = sortedOptions.map((opt, index) => {
        const existingCondition =
          parseDisplayCondition(opt.dogma_displaycondition ?? undefined) || {};

        // Filter validProducts based on option name
        // "Arch Top Sash" / "Arch Bottom Sash" -> Only Arch products (producttypecode 2)
        // "Top Sash" / "Bottom Sash" -> All products EXCEPT Arch
        let filteredValidProducts = validProductsForFrame;
        const optionName = opt.dogma_name.toLowerCase();
        const isArchSash =
          optionName.includes("arch") &&
          (optionName.includes("top sash") ||
            optionName.includes("bottom sash"));
        const isRegularSash =
          !optionName.includes("arch") &&
          (optionName.includes("top sash") ||
            optionName.includes("bottom sash"));

        if (isArchSash) {
          // Only Arch products (producttypecode 2)
          filteredValidProducts = validProductsForFrame.filter((productId) => {
            const product = products.find((p) => p.productid === productId);
            return product && product.producttypecode === 2;
          });
        } else if (isRegularSash) {
          // All products EXCEPT Arch (exclude producttypecode 2)
          filteredValidProducts = validProductsForFrame.filter((productId) => {
            const product = products.find((p) => p.productid === productId);
            return product && product.producttypecode !== 2;
          });
        }

        // Intersect with the option's own existing validForProducts restriction
        if (
          existingCondition.validForProducts &&
          existingCondition.validForProducts.length > 0
        ) {
          const existingSet = new Set(existingCondition.validForProducts);
          filteredValidProducts = filteredValidProducts.filter((id) =>
            existingSet.has(id),
          );
        }

        const mergedCondition = {
          ...existingCondition,
          enabled: true,
          validForProducts: filteredValidProducts,
        };

        return {
          optionId: `${opt.dogma_configurationoptionsid}`,
          optionText: opt.dogma_name,
          description: opt.dogma_description,
          priceModifier:
            opt.dogma_pricemodifier !== 0 ? opt.dogma_pricemodifier : undefined,
          basePrice: opt.dogma_pricemodifier === 0 ? 0 : undefined,
          priceType: undefined,
          responseValue: opt.dogma_name.toLowerCase().replace(/\s+/g, "_"),
          value: opt.dogma_value,
          inputType: opt.dogma_inputtype,
          optionType: optionTypeValue,
          optionTypeName,
          thumbnail: opt.dogma_picturebloburl,
          displayCondition: mergedCondition,
          order: index + 1,
          priceList: parsePriceList(opt.dogma_pricelist),
          optionUnit: opt.dogma_optionunit ?? DEFAULT_OPTION_UNIT,
        };
      });

      // Check if all options have the same display condition
      const { commonCondition: _commonCondition, hasCommonCondition } =
        extractCommonDisplayCondition(preparedOptions);

      // Union all optionIds per categoryId across every option's dependsOn so the
      // question is shown whenever ANY option inside it could be visible. This handles
      // cases like Arch Top Sash (38) where different options depend on different
      // decorative bar IDs — taking only the first option's deps would miss others.
      const commonDependsOn: any[] =
        extractDependsOnUnion(preparedOptions) ??
        (preparedOptions[0]?.displayCondition?.dependsOn || []);

      // Add Configuration Mode "Individual" dependency to per-frame questions (if available)
      const commonWidth = extractCommonWidth(preparedOptions);
      const configModeCondition = {
        enabled: true,
        dependsOn: [
          ...(individualOption && configModeCategoryId
            ? [
                {
                  categoryId: configModeCategoryId,
                  optionIds: [individualOption.dogma_configurationoptionsid],
                },
              ]
            : []),
          ...commonDependsOn,
        ],
        // Always use the pre-computed per-frame product list so the question itself
        // is hidden for products it is not valid for (e.g. Arch excluded from trickle vents).
        validForProducts: validProductsForFrame,
        ...commonWidth,
      };

      // Generate responsePath (use as unique key for step preservation)
      // Use numeric optionTypeValue (not optionTypeName) to avoid collisions when
      // multiple option types fall back to the same "Other" name.
      const responsePath = `${category.dogma_categoryid}.${optionTypeValue ?? 0}.${inputTypeKey}.frame${frameIndex}`;

      const question: IFlowQuestion = {
        questionId: `Q${String(currentQuestionCounter).padStart(4, "0")}`,
        step: getStepForQuestion(responsePath),
        category: category.dogma_categoryid,
        categoryName: category.dogma_name,
        optionType: optionTypeValue || 0,
        optionTypeName,
        question: generateQuestionText(
          category.dogma_name,
          optionTypeName,
          optionTypeValue,
          inputTypeKey,
          frameIndex,
        ),
        helperText: optionTypeValue ? OPTION_TYPE_HELPER_TEXT_MAP[optionTypeValue] : undefined,
        questionType,
        required: true,
        responsePath,
        displayCondition: configModeCondition,
        options: preparedOptions.map((opt) => ({
          ...opt,
          // When dependsOn is lifted to question level, keep the full option condition
          // so validForProducts and width restrictions still filter individual options.
          // Only clear to widthOnly when the entire condition was lifted (hasCommonCondition).
          displayCondition:
            commonDependsOn.length > 0
              ? opt.displayCondition
              : hasCommonCondition
                ? widthOnlyCondition(opt.displayCondition)
                : opt.displayCondition,
        })),
      };

      questions.push(question);
      currentQuestionCounter++;
    }
  } // Close the else block for hasMultiFrameProducts

  // Generate "Same for All" question
  const sameForAllOptions: IFlowOption[] = sortedOptions.map((opt, index) => {
    let displayCondition = parseDisplayCondition(
      opt.dogma_displaycondition ?? undefined,
    );

    // For Sash configuration options (optionType 7), trust the admin's
    // displayCondition as-is — validForProducts empty means all products.
    // For all other optionTypes, apply name-based arch/sash filtering as a
    // fallback when the admin hasn't explicitly restricted products.
    if (displayCondition && optionTypeValue !== 7) {
      const optionName = opt.dogma_name.toLowerCase();
      const isArchSash =
        optionName.includes("arch") &&
        (optionName.includes("top sash") || optionName.includes("bottom sash"));
      const isRegularSash =
        !optionName.includes("arch") &&
        (optionName.includes("top sash") || optionName.includes("bottom sash"));

      const adminSetValidProducts = (displayCondition.validForProducts?.length ?? 0) > 0;

      if (isArchSash && !adminSetValidProducts) {
        const archProductIds = products
          .filter((p) => p.producttypecode === 2)
          .map((p) => p.productid);
        displayCondition = { ...displayCondition, validForProducts: archProductIds };
      } else if (isRegularSash && !adminSetValidProducts) {
        const nonArchProductIds = products
          .filter((p) => p.producttypecode !== 2)
          .map((p) => p.productid);
        displayCondition = { ...displayCondition, validForProducts: nonArchProductIds };
      }
    }

    return {
      optionId: `${opt.dogma_configurationoptionsid}`,
      optionText: opt.dogma_name,
      description: opt.dogma_description,
      priceModifier:
        opt.dogma_pricemodifier !== 0 ? opt.dogma_pricemodifier : undefined,
      basePrice: opt.dogma_pricemodifier === 0 ? 0 : undefined,
      priceType: undefined,
      responseValue: opt.dogma_name.toLowerCase().replace(/\s+/g, "_"),
      value: opt.dogma_value,
      inputType: opt.dogma_inputtype,
      optionType: optionTypeValue,
      optionTypeName,
      thumbnail: opt.dogma_picturebloburl,
      displayCondition,
      order: index + 1,
      priceList: parsePriceList(opt.dogma_pricelist),
      optionUnit: opt.dogma_optionunit ?? DEFAULT_OPTION_UNIT,
    };
  });

  const { hasCommonCondition: hasSameForAllCommon } =
    extractCommonDisplayCondition(sameForAllOptions);

  // Union all optionIds per categoryId across every option's dependsOn so the
  // question is shown whenever ANY option inside it could be visible. This handles
  // cases like Arch Top Sash (38) where different options depend on different
  // decorative bar IDs — taking only the first option's deps would miss others.
  const commonDependsOn: any[] =
    extractDependsOnUnion(sameForAllOptions) ??
    (sameForAllOptions[0]?.displayCondition?.dependsOn || []);

  // For single-frame products: No Configuration Mode dependency (show directly)
  // For multi-frame products: Add Configuration Mode "Same for All" dependency
  // Trickle Vents (category type 8): arch windows have no trickle vents — respect
  // allValidProductIds so arch is excluded when not listed in option display conditions.
  // All other categories: always include every Standard (1) + Arch (2) product because
  // arch absence from option-level validForProducts is a data gap, not an intentional
  // restriction; per-option displayConditions handle further filtering inside the question.
  const singleFrameProductIds =
    category.dogma_categorytype === 8
      ? singleFrameProducts
          .filter((p) => !allValidProductIds || allValidProductIds.has(p.productid))
          .map((p) => p.productid)
      : singleFrameProducts.map((p) => p.productid);
  const multiFrameProductIds = multiFrameProducts
    .filter((p) => !allValidProductIds || allValidProductIds.has(p.productid))
    .map((p) => p.productid);

  const allSuffix = isConditionalGroup ? "_cond_all" : "_all";

  // Check if these options are ONLY for single-frame products (Arch/Standard)
  if (!hasMultiFrameProducts) {
    // Options only valid for Arch/Standard: Skip Configuration Mode, generate simple question
    // Only single-frame products: Create question with no Configuration Mode dependency

    // Filter products based on question type
    // "Top Sash" / "Bottom Sash" -> Exclude Arch products
    // "Arch Top Sash" / "Arch Bottom Sash" -> Only Arch products
    let filteredSingleFrameProductIds = singleFrameProductIds;
    const optionTypeNameLower = optionTypeName.toLowerCase();
    const isArchSashQuestion =
      optionTypeNameLower.includes("arch") &&
      (optionTypeNameLower.includes("top sash") ||
        optionTypeNameLower.includes("bottom sash"));
    const isRegularSashQuestion =
      !optionTypeNameLower.includes("arch") &&
      !optionTypeNameLower.includes("lead") &&
      (optionTypeNameLower.includes("top sash") ||
        optionTypeNameLower.includes("bottom sash"));

    if (isArchSashQuestion) {
      filteredSingleFrameProductIds = singleFrameProducts
        .filter((p) => p.producttypecode === 2)
        .filter(
          (p) => !allValidProductIds || allValidProductIds.has(p.productid),
        )
        .map((p) => p.productid);
    } else if (isRegularSashQuestion) {
      filteredSingleFrameProductIds = singleFrameProducts
        .filter((p) => p.producttypecode !== 2)
        .filter(
          (p) => !allValidProductIds || allValidProductIds.has(p.productid),
        )
        .map((p) => p.productid);
    }

    const singleFrameCondition = {
      enabled: true,
      dependsOn: commonDependsOn,
      validForProducts: filteredSingleFrameProductIds,
      ...extractCommonWidth(sameForAllOptions),
    };

    const responsePath = `${category.dogma_categoryid}.${optionTypeValue ?? 0}.${inputTypeKey}${allSuffix}`;

    const sameForAllQuestion: IFlowQuestion = {
      questionId: `Q${String(currentQuestionCounter).padStart(4, "0")}`,
      step: getStepForQuestion(responsePath),
      category: category.dogma_categoryid,
      categoryName: category.dogma_name,
      optionType: optionTypeValue || 0,
      optionTypeName,
      question: generateQuestionText(
        category.dogma_name,
        optionTypeName,
        optionTypeValue,
        inputTypeKey,
      ),
      helperText: optionTypeValue ? OPTION_TYPE_HELPER_TEXT_MAP[optionTypeValue] : undefined,
      questionType,
      required: true,
      responsePath,
      displayCondition: singleFrameCondition,
      options: sameForAllOptions.map((opt) => ({
        ...opt,
        displayCondition:
          commonDependsOn.length > 0
            ? opt.displayCondition
            : hasSameForAllCommon
              ? widthOnlyCondition(opt.displayCondition)
              : opt.displayCondition,
      })),
    };

    questions.push(sameForAllQuestion);
    currentQuestionCounter++;
  } else {
    // Options are valid for multi-frame products: Use Configuration Mode logic

    if (singleFrameProductIds.length > 0 && multiFrameProductIds.length > 0) {
      // Both single-frame and multi-frame products: Create two separate questions

      // Filter products based on question type
      // "Top Sash" / "Bottom Sash" -> Exclude Arch products
      // "Arch Top Sash" / "Arch Bottom Sash" -> Only Arch products
      let filteredSingleFrameProductIds = singleFrameProductIds;
      const optionTypeNameLower = optionTypeName.toLowerCase();
      const isArchSashQuestion =
        optionTypeNameLower.includes("arch") &&
        (optionTypeNameLower.includes("top sash") ||
          optionTypeNameLower.includes("bottom sash"));
      const isRegularSashQuestion =
        !optionTypeNameLower.includes("arch") &&
        !optionTypeNameLower.includes("lead") &&
        (optionTypeNameLower.includes("top sash") ||
          optionTypeNameLower.includes("bottom sash"));

      if (isArchSashQuestion) {
        filteredSingleFrameProductIds = singleFrameProducts
          .filter((p) => p.producttypecode === 2)
          .filter(
            (p) => !allValidProductIds || allValidProductIds.has(p.productid),
          )
          .map((p) => p.productid);
      } else if (isRegularSashQuestion) {
        filteredSingleFrameProductIds = singleFrameProducts
          .filter((p) => p.producttypecode !== 2)
          .filter(
            (p) => !allValidProductIds || allValidProductIds.has(p.productid),
          )
          .map((p) => p.productid);
      }

      // Question 1: For single-frame products (no Configuration Mode dependency)
      const singleFrameCondition = {
        enabled: true,
        dependsOn: commonDependsOn,
        validForProducts: filteredSingleFrameProductIds,
        ...extractCommonWidth(sameForAllOptions),
      };

      // Generate responsePath for single-frame question (use as unique key for step preservation)
      // Use numeric optionTypeValue (not optionTypeName) to avoid collisions when
      // multiple option types fall back to the same "Other" name.
      const responsePathSf = `${category.dogma_categoryid}.${optionTypeValue ?? 0}.${inputTypeKey}${allSuffix}_sf`;

      const singleFrameQuestion: IFlowQuestion = {
        questionId: `Q${String(currentQuestionCounter).padStart(4, "0")}`,
        step: getStepForQuestion(responsePathSf),
        category: category.dogma_categoryid,
        categoryName: category.dogma_name,
        optionType: optionTypeValue || 0,
        optionTypeName,
        question: generateQuestionText(
          category.dogma_name,
          optionTypeName,
          optionTypeValue,
          inputTypeKey,
        ),
        helperText: optionTypeValue ? OPTION_TYPE_HELPER_TEXT_MAP[optionTypeValue] : undefined,
        questionType,
        required: true,
        responsePath: responsePathSf,
        displayCondition: singleFrameCondition,
        options: sameForAllOptions.map((opt) => ({
          ...opt,
          displayCondition: hasSameForAllCommon
            ? widthOnlyCondition(opt.displayCondition)
            : opt.displayCondition,
        })),
      };

      questions.push(singleFrameQuestion);
      currentQuestionCounter++;
    }

    // Question for multi-frame products: With Configuration Mode dependency
    const multiFrameCondition = {
      enabled: true,
      dependsOn: [
        ...(sameForAllOption && configModeCategoryId
          ? [
              {
                categoryId: configModeCategoryId,
                optionIds: [sameForAllOption.dogma_configurationoptionsid],
              },
            ]
          : []),
        ...commonDependsOn,
      ],
      validForProducts: multiFrameProductIds,
      ...extractCommonWidth(sameForAllOptions),
    };

    // Generate responsePath for multi-frame question (use as unique key for step preservation)
    // Use numeric optionTypeValue (not optionTypeName) to avoid collisions when
    // multiple option types fall back to the same "Other" name.
    const responsePathMf = `${category.dogma_categoryid}.${optionTypeValue ?? 0}.${inputTypeKey}${allSuffix}_mf`;

    const sameForAllQuestion: IFlowQuestion = {
      questionId: `Q${String(currentQuestionCounter).padStart(4, "0")}`,
      step: getStepForQuestion(responsePathMf),
      category: category.dogma_categoryid,
      categoryName: category.dogma_name,
      optionType: optionTypeValue || 0,
      optionTypeName,
      question: generateQuestionText(
        category.dogma_name,
        optionTypeName,
        optionTypeValue,
        inputTypeKey,
      ),
      helperText: optionTypeValue ? OPTION_TYPE_HELPER_TEXT_MAP[optionTypeValue] : undefined,
      questionType,
      required: true,
      responsePath: responsePathMf,
      displayCondition: multiFrameCondition,
      options: sameForAllOptions.map((opt) => ({
        ...opt,
        displayCondition:
          commonDependsOn.length > 0
            ? opt.displayCondition
            : hasSameForAllCommon
              ? widthOnlyCondition(opt.displayCondition)
              : opt.displayCondition,
      })),
    };

    // Only generate the "Same for All" multi-frame question when a Configuration
    // Mode option (optionType 49) exists in this category. Without it there is
    // no way for the user to choose "Same for All", so generating the question
    // would create a redundant frameless duplicate alongside the per-frame ones
    // (e.g. trickle vents always ask per-frame and have no config-mode option).
    if (sameForAllOption) {
      questions.push(sameForAllQuestion);
      currentQuestionCounter++;
    }
  }

  return {
    questions,
    newQuestionCounter: currentQuestionCounter,
  };
};;

/**
 * Helper: Map input type key to numeric input type value
 * Input types from ALL_INPUT_TYPES:
 * 1 = Text Input, 2 = Number Input, 3 = Yes/No (Boolean),
 * 4 = Multi Select, 5 = Single Select, 6 = Single Select with Images,
 * 7 = Colour Swatch, 8 = Visual Selector, 9 = Colour Picker
 */
export const mapInputTypeToQuestionType = (
  inputType: string | number
): number => {
  // If already a number, return it
  if (typeof inputType === "number") return inputType;

  // Check if it's a numeric string (e.g., "1", "2", "3")
  const numericValue = parseInt(inputType, 10);
  if (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 9) {
    return numericValue;
  }

  // Otherwise, map from string keys
  const mapping: Record<string, number> = {
    text_input: 1,
    number_input: 2,
    boolean: 3,
    multi_select: 4,
    single_select: 5,
    single_select_with_image: 6,
    swatch_select: 7,
    visual_select: 8,
    color_picker: 9,
  };
  return mapping[inputType] || 5; // Default to single_select (5)
};

/**
 * Helper: Normalize display condition for comparison
 * Sorts arrays to ensure order-independent comparison
 */
const normalizeDisplayCondition = (condition: any): any => {
  if (!condition) return null;

  const normalized: any = { ...condition };

  // Sort validForProducts array if it exists
  if (Array.isArray(normalized.validForProducts)) {
    normalized.validForProducts = [...normalized.validForProducts].sort();
  }

  // Sort dependsOn array and nested optionIds arrays if they exist
  if (Array.isArray(normalized.dependsOn)) {
    normalized.dependsOn = normalized.dependsOn.map((dep: any) => {
      const normalizedDep = { ...dep };
      if (Array.isArray(normalizedDep.optionIds)) {
        normalizedDep.optionIds = [...normalizedDep.optionIds].sort();
      }
      return normalizedDep;
    }).sort((a: any, b: any) => {
      // Sort by categoryId
      return (a.categoryId || '').localeCompare(b.categoryId || '');
    });
  }

  return normalized;
};

/**
 * Helper: Extract common display condition from options
 */
export const extractCommonDisplayCondition = (
  optionsList: Array<{ displayCondition?: any; [key: string]: any }>
): { commonCondition: any | null; hasCommonCondition: boolean } => {
  if (optionsList.length === 0) {
    return { commonCondition: null, hasCommonCondition: false };
  }

  const firstOption = optionsList[0];
  if (!firstOption) {
    return { commonCondition: null, hasCommonCondition: false };
  }
  const firstCondition = firstOption.displayCondition;

  // Normalize the first condition for comparison
  const normalizedFirstCondition = normalizeDisplayCondition(firstCondition);

  const allSame = optionsList.every((opt) => {
    const currentCondition = opt.displayCondition;

    if (!firstCondition && !currentCondition) return true;
    if (!firstCondition || !currentCondition) return false;

    // Normalize current condition and compare
    const normalizedCurrentCondition = normalizeDisplayCondition(currentCondition);
    return JSON.stringify(normalizedFirstCondition) === JSON.stringify(normalizedCurrentCondition);
  });

  if (allSame && firstCondition) {
    return { commonCondition: firstCondition, hasCommonCondition: true };
  }

  return { commonCondition: null, hasCommonCondition: false };
};

/**
 * Helper: Extract common dependsOn intersection across options.
 *
 * For each categoryId that appears in every option's dependsOn, computes
 * the intersection of optionIds and returns those entries. Useful for
 * lifting shared dependencies up to the question level even when full
 * displayConditions differ between options.
 *
 * Returns null if:
 * - any option has no dependsOn,
 * - no categoryId is shared across all options (different categoryIds → skip),
 * - the intersection of optionIds is empty for every shared categoryId.
 */
export const extractCommonDependsOnIntersection = (
  optionsList: Array<{ displayCondition?: any; [key: string]: any }>
): any[] | null => {
  if (optionsList.length === 0) return null;

  const firstDependsOn = optionsList[0]?.displayCondition?.dependsOn;
  if (!Array.isArray(firstDependsOn) || firstDependsOn.length === 0) {
    return null;
  }

  const allHaveDependsOn = optionsList.every((opt) => {
    const d = opt?.displayCondition?.dependsOn;
    return Array.isArray(d) && d.length > 0;
  });
  if (!allHaveDependsOn) return null;

  const commonDependsOn: any[] = [];

  for (const firstDep of firstDependsOn) {
    const categoryId = firstDep?.categoryId;
    if (!categoryId || !Array.isArray(firstDep.optionIds)) continue;

    let intersection = new Set<string>(firstDep.optionIds);
    let allHaveCategory = true;

    for (let i = 1; i < optionsList.length; i++) {
      const otherDepsOn = optionsList[i]?.displayCondition?.dependsOn || [];
      const matching = otherDepsOn.find(
        (d: any) => d?.categoryId === categoryId
      );
      if (!matching || !Array.isArray(matching.optionIds)) {
        allHaveCategory = false;
        break;
      }
      const otherIds = new Set<string>(matching.optionIds);
      intersection = new Set(
        [...intersection].filter((id) => otherIds.has(id))
      );
      if (intersection.size === 0) break;
    }

    if (allHaveCategory && intersection.size > 0) {
      commonDependsOn.push({
        categoryId,
        optionIds: Array.from(intersection),
      });
    }
  }

  return commonDependsOn.length > 0 ? commonDependsOn : null;
};

/**
 * Helper: Extract common dependsOn from options
 * Returns the dependsOn array if all options share the same dependencies
 */
export const extractCommonDependsOn = (
  optionsList: Array<{ displayCondition?: any; [key: string]: any }>
): any[] | null => {
  if (optionsList.length === 0) {
    return null;
  }

  const firstOption = optionsList[0];

  if (!firstOption || !firstOption.displayCondition?.dependsOn) {
    return null;
  }

  const firstDependsOn = firstOption.displayCondition.dependsOn;

  // Check if all options have the same dependsOn
  const allSameDependsOn = optionsList.every((opt) => {
    const currentDependsOn = opt.displayCondition?.dependsOn;

    if (!firstDependsOn && !currentDependsOn) return true;
    if (!firstDependsOn || !currentDependsOn) return false;

    return JSON.stringify(firstDependsOn) === JSON.stringify(currentDependsOn);
  });

  if (allSameDependsOn && firstDependsOn) {
    return firstDependsOn;
  }

  return null;
};

/**
 * Helper: Union all optionIds per categoryId across options that have dependsOn.
 * Returns null when any option lacks a dependsOn (question must always be visible).
 *
 * Correct question-level rule: show the question whenever ANY option inside it
 * could be visible. If 8 options depend on [a,b,c] and 1 depends on [a], the
 * union is [a,b,c] — the question is hidden only when none of a/b/c is selected.
 */
export const extractDependsOnUnion = (
  optionsList: Array<{ displayCondition?: any; [key: string]: any }>
): any[] | null => {
  if (optionsList.length === 0) return null;

  // If any option has no dependsOn it's always visible → don't restrict question
  const allHaveDependsOn = optionsList.every((opt) => {
    const d = opt?.displayCondition?.dependsOn;
    return Array.isArray(d) && d.length > 0;
  });
  if (!allHaveDependsOn) return null;

  const byCategory = new Map<string, Set<string>>();
  const byCategoryCount = new Map<string, number>();
  const total = optionsList.length;

  for (const opt of optionsList) {
    for (const dep of opt?.displayCondition?.dependsOn ?? []) {
      if (!dep?.categoryId || !Array.isArray(dep.optionIds)) continue;
      if (!byCategory.has(dep.categoryId)) {
        byCategory.set(dep.categoryId, new Set());
        byCategoryCount.set(dep.categoryId, 0);
      }
      dep.optionIds.forEach((id: string) => byCategory.get(dep.categoryId)!.add(id));
      byCategoryCount.set(dep.categoryId, (byCategoryCount.get(dep.categoryId) ?? 0) + 1);
    }
  }

  // Only keep categories referenced by ALL options — avoids creating spurious AND
  // conditions from categories that only some options depend on. Example: if most
  // glazing pattern options only depend on [obscure=yes] but "Everglade" also depends
  // on [glass-type=obscure-toughened], the glass-type entry must NOT be lifted to the
  // question level (it would hide the whole question when glass-type isn't yet selected).
  const result = Array.from(byCategory.entries())
    .filter(([categoryId]) => (byCategoryCount.get(categoryId) ?? 0) >= total)
    .map(([categoryId, optionIds]) => ({
      categoryId,
      optionIds: Array.from(optionIds),
    }));
  return result.length > 0 ? result : null;
};

/**
 * Helper: Generate question text.
 * Checks OPTION_TYPE_QUESTION_MAP first; falls back to generic templates
 * that include the category name when no specific mapping is found.
 */
export const generateQuestionText = (
  categoryName: string,
  optionTypeName: string,
  optionTypeValue?: number,
  inputType?: string | number,
  frameIndex?: number
): string => {
  // Use the standard map when available
  if (optionTypeValue && OPTION_TYPE_QUESTION_MAP[optionTypeValue]) {
    return OPTION_TYPE_QUESTION_MAP[optionTypeValue](frameIndex, categoryName);
  }

  const frameText = frameIndex ? ` Frame ${frameIndex}` : "";
  const categoryLower = categoryName.toLowerCase();

  // Convert numeric input type to comparable value
  const isTextInput =
    inputType === "text_input" || inputType === 1 || inputType === "1";
  const isNumberInput =
    inputType === "number_input" || inputType === 2 || inputType === "2";
  const isBoolean =
    inputType === "boolean" || inputType === 3 || inputType === "3";

  if (isTextInput) {
    if (optionTypeName === "General" || optionTypeName === "Other") {
      return `Please enter ${categoryLower}${frameText ? " for" + frameText : ""}`;
    }
    return `Please enter ${optionTypeName.toLowerCase()} for ${categoryLower}${frameText}`;
  }

  if (isNumberInput) {
    if (optionTypeName === "General" || optionTypeName === "Other") {
      return `Please enter ${categoryLower}${frameText ? " for" + frameText : ""}`;
    }
    return `Enter ${optionTypeName.toLowerCase()} for ${categoryLower}${frameText}`;
  }

  if (isBoolean) {
    if (optionTypeName === "General" || optionTypeName === "Other") {
      return `Would you like ${categoryLower}${frameText ? " for" + frameText : ""}?`;
    }
    return `Would you like ${optionTypeName.toLowerCase()} for ${categoryLower}${frameText}?`;
  }

  if (optionTypeName === "General" || optionTypeName === "Other") {
    return `What ${categoryLower} would you like${frameText ? " for" + frameText : ""}?`;
  }

  return `Select your ${optionTypeName.toLowerCase()} for ${categoryLower}${frameText}`;
};
